import {
  CCTV_UNAVAILABLE_LABEL,
  DEVICE_TYPES,
  isUnavailableCctv,
  markerOpacity,
  markerPaint,
  type ModelDevicePoint,
} from "./deviceTypes";
import {
  ICON_CENTER_X,
  ICON_CENTER_Y,
  ICON_PATH_SCALE,
  ICON_RADIUS,
  ICON_SHADOW_BLUR,
  ICON_SHADOW_COLOR,
  ICON_STROKE_WIDTH,
} from "./deviceAppearance";
import { SEVERITY_LABELS } from "./severity";
import type { DeviceStatus } from "@safers/api";
import "./modelMarkers.css";

/**
 * 장비 핀 버튼 — 2D 지도와 3D 뷰어가 장비 자리에 똑같이 올린다.
 */
export interface DevicePin {
  button: HTMLButtonElement;
  title: HTMLSpanElement;
  /** 아이콘을 DOM 으로 그릴 때(2D)만 있다. 3D 는 아이콘을 WebGL 스프라이트로 그린다. */
  icon: { element: HTMLSpanElement; size: number; svg: string } | null;
}

export function createDevicePin(
  deviceId: string,
  onSelect: (id: string, button: HTMLButtonElement) => void,
  options: { iconSize?: number } = {}
): DevicePin {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "model-device-hit";
  button.dataset.modelDeviceId = deviceId;
  let icon: DevicePin["icon"] = null;
  if (options.iconSize) {
    const element = document.createElement("span");
    element.className = "model-device-icon";
    element.setAttribute("aria-hidden", "true");
    button.appendChild(element);

    const circle = deviceIconCircleSize(options.iconSize);
    button.style.width = `${circle}px`;
    button.style.height = `${circle}px`;
    icon = { element, size: options.iconSize, svg: "" };
  }
  const title = document.createElement("span");
  title.className = "model-device-title";
  button.appendChild(title);
  button.addEventListener("pointerdown", (event) => event.stopPropagation());
  button.addEventListener("click", () => onSelect(deviceId, button));
  return { button, title, icon };
}

/** 아이콘 SVG 의 반폭 — 원에 테두리 두께와 그림자 여백을 더한다. */
const ICON_SVG_HALF = ICON_RADIUS + ICON_STROKE_WIDTH / 2 + ICON_SHADOW_BLUR * 2;

/** 아이콘 SVG 안에서 원이 차지하는 지름(px) — 2D 핀 버튼을 3D 처럼 원 크기로 맞춘다. */
const deviceIconCircleSize = (size: number) => (size * ICON_RADIUS) / ICON_SVG_HALF;

/** 3D 마커 텍스처와 같은 모양의 아이콘 SVG (2D 지도용) */
function deviceIconSvg(device: ModelDevicePoint, size: number): string {
  const half = ICON_SVG_HALF;
  const offset = 12 * ICON_PATH_SCALE;
  const paint = markerPaint(device);
  return `<svg width="${size}" height="${size}" viewBox="${ICON_CENTER_X - half} ${ICON_CENTER_Y - half} ${half * 2} ${half * 2}" opacity="${markerOpacity(device)}" aria-hidden="true">
    <circle cx="${ICON_CENTER_X}" cy="${ICON_CENTER_Y}" r="${ICON_RADIUS}" fill="${paint.fill}" stroke="${paint.ring}" stroke-width="${ICON_STROKE_WIDTH}" style="filter:drop-shadow(0 0 ${ICON_SHADOW_BLUR / 2}px ${ICON_SHADOW_COLOR})"/>
    <path d="${DEVICE_TYPES[device.type].path}" transform="translate(${ICON_CENTER_X - offset} ${ICON_CENTER_Y - offset}) scale(${ICON_PATH_SCALE})" fill="none" stroke="${paint.icon}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
  </svg>`;
}

const STATUS_ARIA_TEXT: Record<DeviceStatus, string | null> = {
  ACTIVE: null,
  DELAYED: "통신 지연",
  OFFLINE: "통신 끊김",
};

/** 핀을 다시 만들지 않고 장비 상태에 맞춘다 — 팝업이 붙은 버튼이 그대로 남는다. */
export function updateDevicePin(pin: DevicePin, device: ModelDevicePoint) {
  const { button, title, icon } = pin;
  title.textContent = device.name || device.deviceId;
  if (icon) {
    const svg = deviceIconSvg(device, icon.size);
    if (icon.svg !== svg) {
      icon.element.innerHTML = svg;
      icon.svg = svg;
    }
  }
  button.setAttribute(
    "aria-label",
    [
      device.name || device.deviceId,
      !device.statusLabel && device.status && STATUS_ARIA_TEXT[device.status],
      device.statusLabel ?? SEVERITY_LABELS[device.severity],
    ]
      .filter(Boolean)
      .join(" · ")
  );
  button.style.outline = device.selected ? "3px solid #ff7500" : "";
  if (isUnavailableCctv(device)) {
    button.setAttribute("aria-disabled", "true");
    button.title = CCTV_UNAVAILABLE_LABEL;
  } else {
    button.removeAttribute("aria-disabled");
    button.removeAttribute("title");
  }
  if (device.selected != null) {
    button.setAttribute("aria-pressed", String(device.selected));
    button.removeAttribute("aria-haspopup");
  } else {
    button.removeAttribute("aria-pressed");
    if (device.type !== "CCTV") button.setAttribute("aria-haspopup", "dialog");
    else button.removeAttribute("aria-haspopup");
  }
}
